/**
 * Lazy Loading with Blur Effect
 * Inspired by Booster Theme approach
 */

(function() {
  'use strict';

  // Configuration
  const config = {
    rootMargin: '50px 0px',
    threshold: 0.01,
    loadedClass: 'lazyloaded',
    loadingClass: 'lazyloading', 
    errorClass: 'lazyerror'
  };

  // Check if native lazy loading is supported
  const supportsLazyLoading = 'loading' in HTMLImageElement.prototype;

  /**
   * Load image or video
   */
  function loadMedia(element) {
    if (element.tagName === 'IMG') {
      loadImage(element);
    } else if (element.tagName === 'VIDEO') {
      loadVideo(element);
    } else if (element.tagName === 'IFRAME') {
      loadIframe(element);
    } else if (element.dataset.bgSrc) {
      loadBackgroundImage(element);
    }
  }

  /**
   * Load image with fade-in effect
   */
  function loadImage(img) {
    const src = img.dataset.src;
    const srcset = img.dataset.srcset;
    const sizes = img.dataset.sizes;

    if (!src) return;

    // Add loading class
    img.classList.add(config.loadingClass);

    // Create a new image to preload
    const tempImg = new Image();
    
    tempImg.onload = function() {
      // Set the actual image sources
      if (src) img.src = src;
      if (srcset) img.srcset = srcset;
      if (sizes) img.sizes = sizes;
      
      // Remove lazy class and add loaded class
      img.classList.remove('lazyload', config.loadingClass);
      img.classList.add(config.loadedClass);
      
      // Remove data attributes
      delete img.dataset.src;
      delete img.dataset.srcset;
      delete img.dataset.sizes;
      
      // Fire custom event
      img.dispatchEvent(new CustomEvent('lazyloaded', { 
        bubbles: true,
        detail: { target: img }
      }));
    };

    tempImg.onerror = function() {
      img.classList.remove('lazyload', config.loadingClass);
      img.classList.add(config.errorClass);
      
      // Fire error event
      img.dispatchEvent(new CustomEvent('lazyerror', {
        bubbles: true,
        detail: { target: img }
      }));
    };

    // Start loading
    if (srcset) tempImg.srcset = srcset;
    tempImg.src = src;
  }

  /**
   * Load video
   */
  function loadVideo(video) {
    const sources = video.querySelectorAll('source[data-src]');
    
    sources.forEach(source => {
      source.src = source.dataset.src;
      delete source.dataset.src;
    });

    if (video.dataset.src) {
      video.src = video.dataset.src;
      delete video.dataset.src;
    }

    if (video.dataset.poster) {
      video.poster = video.dataset.poster;
      delete video.dataset.poster;
    }

    video.load();
    
    video.classList.remove('lazyload');
    video.classList.add(config.loadedClass);
    
    video.dispatchEvent(new CustomEvent('lazyloaded', {
      bubbles: true,
      detail: { target: video }
    }));
  }

  /**
   * Load iframe
   */
  function loadIframe(iframe) {
    if (iframe.dataset.src) {
      iframe.src = iframe.dataset.src;
      delete iframe.dataset.src;
      
      iframe.classList.remove('lazyload');
      iframe.classList.add(config.loadedClass);
      
      iframe.dispatchEvent(new CustomEvent('lazyloaded', {
        bubbles: true,
        detail: { target: iframe }
      }));
    }
  }

  /**
   * Load background image
   */
  function loadBackgroundImage(element) {
    const bgSrc = element.dataset.bgSrc;
    
    if (!bgSrc) return;

    const tempImg = new Image();
    
    tempImg.onload = function() {
      element.style.backgroundImage = `url(${bgSrc})`;
      element.classList.remove('lazyload');
      element.classList.add(config.loadedClass);
      
      delete element.dataset.bgSrc;
      
      element.dispatchEvent(new CustomEvent('lazyloaded', {
        bubbles: true,
        detail: { target: element }
      }));
    };

    tempImg.src = bgSrc;
  }

  /**
   * Initialize Intersection Observer
   */
  function initIntersectionObserver() {
    const lazyMediaObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          loadMedia(element);
          observer.unobserve(element);
        }
      });
    }, config);

    return lazyMediaObserver;
  }

  /**
   * Initialize lazy loading
   */
  function init() {
    // Get all lazy elements
    const lazyElements = document.querySelectorAll('.lazyload');
    
    if (lazyElements.length === 0) return;

    // Use Intersection Observer if available
    if ('IntersectionObserver' in window) {
      const observer = initIntersectionObserver();
      
      lazyElements.forEach(element => {
        // Skip if already loaded
        if (element.classList.contains(config.loadedClass)) return;
        
        // Special handling for images with native lazy loading
        if (supportsLazyLoading && element.tagName === 'IMG' && element.loading === 'lazy') {
          // Let browser handle it but still add our loaded class when done
          element.addEventListener('load', () => {
            element.classList.remove('lazyload');
            element.classList.add(config.loadedClass);
          });
        } else {
          observer.observe(element);
        }
      });
    } else {
      // Fallback for older browsers - load all immediately
      lazyElements.forEach(element => {
        loadMedia(element);
      });
    }
  }

  /**
   * Reinitialize for dynamically added content
   */
  function refresh() {
    init();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Reinitialize on Shopify events
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:reorder', refresh);
  
  // Handle cart drawer updates
  document.addEventListener('cart:updated', refresh);
  document.addEventListener('cart-drawer:updated', refresh);
  
  // Force reload images in cart drawer if they're stuck
  document.addEventListener('cart-drawer:opened', function() {
    setTimeout(function() {
      const cartDrawerImages = document.querySelectorAll('.cart-drawer .upsell__image img');
      cartDrawerImages.forEach(img => {
        if (img.dataset.src && !img.src.includes(img.dataset.src)) {
          // Force load the image
          loadImage(img);
        }
      });
    }, 100);
  });
  
  // Expose API
  window.LazyLoad = {
    init: init,
    refresh: refresh,
    loadMedia: loadMedia,
    forceLoadElement: function(element) {
      if (element.classList.contains('lazyload')) {
        loadMedia(element);
      }
    }
  };
  
  // Setup MutationObserver for cart drawer
  function setupCartDrawerObserver() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    
    const observer = new MutationObserver(function(mutations) {
      let hasNewImages = false;
      
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            const lazyImages = node.querySelectorAll ? node.querySelectorAll('.lazyload') : [];
            if (lazyImages.length > 0 || node.classList?.contains('lazyload')) {
              hasNewImages = true;
            }
          }
        });
      });
      
      if (hasNewImages) {
        setTimeout(refresh, 50);
      }
    });
    
    observer.observe(cartDrawer, {
      childList: true,
      subtree: true
    });
  }
  
  // Initialize cart drawer observer when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupCartDrawerObserver);
  } else {
    setupCartDrawerObserver();
  }

  // Auto-refresh when new content is added via AJAX
  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function() {
    this.addEventListener('load', function() {
      // Check if this is a cart update request
      if (this.responseURL && (this.responseURL.includes('/cart/') || this.responseURL.includes('cart-drawer'))) {
        setTimeout(function() {
          refresh();
          // Also dispatch custom event for cart updates
          document.dispatchEvent(new CustomEvent('cart:updated'));
        }, 150);
      } else {
        setTimeout(refresh, 100);
      }
    });
    originalOpen.apply(this, arguments);
  };
})();